import React, { createContext, useContext, useMemo } from "react";
import type { Lang, PageKey } from "./types";
import esDictionary from "./es";

export type { Lang, PageKey, SeoConfig } from "./types";
export { LANGS } from "./types";
export {
  BASE_URL,
  ROUTES,
  ALL_ROUTES,
  pathFor,
  urlFor,
  resolveLocation,
} from "./routes";
export { SEO_CONFIG } from "./seo";
export {
  resolveInitialLocation,
  readLangPreference,
  writeLangPreference,
  browserPrefersSpanish,
  isAutomatedAgent,
} from "./detect";

// =========================================================================
// TRANSLATION
// =========================================================================

/**
 * Dictionaries are keyed by the English source string. English therefore has
 * no dictionary at all — it is the identity translation — which means the
 * English site can never regress because of a missing or malformed entry.
 */
const DICTIONARIES: Partial<Record<Lang, Record<string, string>>> = {
  es: esDictionary,
};

// Dev-only: warn once per untranslated string so gaps surface while building
// rather than as stray English on a Spanish page in production.
const warned = new Set<string>();

export function translate(source: string, lang: Lang): string {
  if (lang === "en") return source;
  const dict = DICTIONARIES[lang];
  const hit = dict?.[source];
  if (hit !== undefined) return hit;
  if (process.env.NODE_ENV === "development" && !warned.has(source)) {
    warned.add(source);
    // eslint-disable-next-line no-console
    console.warn(`[i18n] Missing ${lang} translation for: "${source}"`);
  }
  return source;
}

// =========================================================================
// CONTEXT
// =========================================================================

export type Translator = (source: string) => string;

type LanguageContextValue = {
  lang: Lang;
  t: Translator;
  /** Navigate to the equivalent URL of the current page in another language. */
  setLang: (lang: Lang) => void;
};

const LanguageContext = createContext<LanguageContextValue>({
  lang: "en",
  t: (s) => s,
  setLang: () => {},
});

export function LanguageProvider({
  lang,
  setLang,
  children,
}: {
  lang: Lang;
  setLang: (lang: Lang) => void;
  children: React.ReactNode;
}) {
  const value = useMemo<LanguageContextValue>(
    () => ({ lang, setLang, t: (source: string) => translate(source, lang) }),
    [lang, setLang]
  );
  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

/** Full language context: current lang, translator, and the language switcher. */
export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}

/** Shorthand for components that only need to translate strings. */
export function useT(): Translator {
  return useContext(LanguageContext).t;
}

/**
 * Language-aware `<html lang>` value. Kept here so the SEO manager and any
 * future prerender hook agree on the same tag.
 */
export function htmlLangAttr(lang: Lang): string {
  return lang === "es" ? "es" : "en";
}

/** og:locale value for a language. */
export function ogLocale(lang: Lang): string {
  return lang === "es" ? "es_US" : "en_US";
}

export type { PageKey as Page };
